<template>

  <div class="user-app-setup-guide">

    <v-container fluid class="d-flex align-center justify-center" v-if="isDataLoading">
      <v-progress-circular indeterminate :size="50" style="margin:20px 0" color="primary" />
    </v-container>

    <template v-else>
      <v-row class="mb-4" v-if="showDeliveryMessage">
        <v-col align="center">
          <div class="d-flex align-center justify-center">
            <v-icon style="font-size:30px;" color="amber" class="mr-2">mdi-alert-outline</v-icon>
            <span class="title">Set up your account before you can create and handle deliveries.</span>
          </div>
        </v-col>
      </v-row>

      <!------------------------------->
      <!-- Org Account Setup Actions -->
      <!------------------------------->
      <v-container fluid class="message-container mb-8" v-if="showOrgSetup && isOrgAdmin && !isOrgSetupComplete">
        <v-row>
          <v-col class="d-flex align-center justify-sm-start justify-md-center">
            <v-icon large color="#c9c9c9" class="mr-4">mdi-playlist-check</v-icon>
            <div class="message-heading">Finish setting up your organization and account:</div>
          </v-col>
        </v-row>
        <v-row class="mt-4 pb-3">
          <v-col class="px-4">
            <!-- Action: Connect to a provider -->
            <div class="d-flex align-center">
              <v-icon
                  :color="(hasProviderConnections) ? 'green' : 'grey darken-1'"
                  class="action-item-icon mr-4">
                {{ (hasProviderConnections) ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
              </v-icon>
              <div class="action-item-text mr-8" :class="(hasProviderConnections) ? 'completed' : ''">
                Connect to an order provider (LeafLink)
              </div>
              <router-link
                  v-if="!hasProviderConnections"
                  :to="{ name: 'provider-connection-list' }">
                <v-btn outlined color="amber">
                  Connect
                </v-btn>
              </router-link>
              <div class="action-resolved-text" v-else>
                {{ providerConnectionItems[0].name }}
              </div>
            </div>

            <!-- Action: Configure shipper licenses -->
            <div class="d-flex align-center mt-6">
              <v-icon
                  :color="(licensesRequireSetup) ? 'grey darken-1' : 'green'"
                  class="action-item-icon mr-4">
                {{ (licensesRequireSetup) ? 'mdi-circle-outline' : 'mdi-check-circle-outline' }}
              </v-icon>
              <div class="action-item-text mr-8" :class="(!licensesRequireSetup) ? 'completed' : ''">
                Configure FA license(s) for your shipper location
              </div>
              <router-link
                  v-if="licensesRequireSetup"
                  :to="{ name: 'shipper-item', params: { shipperId: shipperId } }">
                <v-btn outlined color="amber">
                  Configure
                </v-btn>
              </router-link>
              <div class="action-resolved-text" v-else>
                configured
              </div>
            </div>

            <!-- Action: Add vehicles -->
            <div class="d-flex align-center mt-6">
              <v-icon
                  :color="(hasVehicles) ? 'green' : 'grey darken-1'"
                  class="action-item-icon mr-4">
                {{ (hasVehicles) ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
              </v-icon>
              <div class="action-item-text mr-8" :class="(hasVehicles) ? 'completed' : ''">
                Add your vehicles
              </div>
              <router-link
                  v-if="!hasVehicles"
                  :to="{ name: 'vehicle-list' }">
                <v-btn outlined color="amber">
                  Add vehicles
                </v-btn>
              </router-link>
              <div class="action-resolved-text" v-else>
                added
              </div>
            </div>

            <!-- Action: Add drivers -->
            <div class="d-flex align-center mt-6">
              <v-icon
                  :color="(hasDrivers) ? 'green' : 'grey darken-1'"
                  class="action-item-icon mr-4">
                {{ (hasDrivers) ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
              </v-icon>
              <div class="action-item-text mr-8" :class="(hasDrivers) ? 'completed' : ''">
                Add drivers to your org
              </div>
              <router-link
                  v-if="!hasDrivers"
                  :to="{ name: 'user-list' }">
                <v-btn outlined color="amber">
                  Add drivers
                </v-btn>
              </router-link>
              <div class="action-resolved-text" v-else>
                added
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!---------------------------------->
      <!-- Driver Profile Setup Actions -->
      <!---------------------------------->
      <v-container fluid class="message-container mb-8" v-if="showDriverSetup && isDriver && !isDriverSetupComplete">
        <v-row>
          <v-col class="d-flex align-center justify-sm-start justify-md-center">
            <v-icon large color="#c9c9c9" class="mr-4">mdi-playlist-check</v-icon>
            <div class="message-heading">To accept deliveries as a driver:</div>
          </v-col>
        </v-row>
        <v-row class="mt-4 pb-3">
          <v-col class="px-4">
            <!-- Action: Save signature -->
            <div class="d-flex align-center">
              <v-icon
                  :color="(hasSavedSignature) ? 'green' : 'grey darken-1'"
                  class="action-item-icon mr-4">
                {{ (hasSavedSignature) ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
              </v-icon>
              <div class="action-item-text mr-8" :class="(hasSavedSignature) ? 'completed' : ''">
                Save your signature for transactions
              </div>
              <router-link
                  v-if="!hasSavedSignature"
                  :to="{ name: 'user-settings-profile' }">
                <v-btn outlined color="amber">
                  User profile
                </v-btn>
              </router-link>
              <div class="action-resolved-text" v-else>
                saved
              </div>
            </div>

            <!-- Action: Upload drivers license -->
            <div class="d-flex align-center mt-6">
              <v-icon
                  :color="(hasSavedDriversLicense) ? 'green' : 'grey darken-1'"
                  class="action-item-icon mr-4">
                {{ (hasSavedDriversLicense) ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
              </v-icon>
              <div class="action-item-text mr-8" :class="(hasSavedDriversLicense) ? 'completed' : ''">
                Upload a valid driver's license
              </div>
              <router-link
                  v-if="!hasSavedDriversLicense"
                  :to="{ name: 'user-settings-driver' }">
                <v-btn outlined color="amber">
                  Driver profile
                </v-btn>
              </router-link>
              <div class="action-resolved-text" v-else>
                uploaded
              </div>
            </div>

            <!-- Action: Upload FA license -->
            <div class="d-flex align-center mt-6">
              <v-icon
                  :color="(hasSavedFaLicense) ? 'green' : 'grey darken-1'"
                  class="action-item-icon mr-4">
                {{ (hasSavedFaLicense) ? 'mdi-check-circle-outline' : 'mdi-circle-outline' }}
              </v-icon>
              <div class="action-item-text mr-8" :class="(hasSavedFaLicense) ? 'completed' : ''">
                Upload a valid facility agent license
              </div>
              <router-link
                  v-if="!hasSavedFaLicense"
                  :to="{ name: 'user-settings-facility-agent' }">
                <v-btn outlined color="amber">
                  Facility Agent profile
                </v-btn>
              </router-link>
              <div class="action-resolved-text" v-else>
                uploaded
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </div>

</template>

<script>
export default {
  name: 'UserAppSetupGuide',

  props: {
    showOrgSetup: {
      type: Boolean,
      default: true
    },
    showDriverSetup: {
      type: Boolean,
      default: true
    },
    showDeliveryMessage: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isDataLoading: false,
      memberData: null,
      savedDriversLicense: null,
      savedFaLicense: null
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    activeUser () {
      return this.$store.getters['auth/activeUser']
    },
    isOrgAdmin () {
      return this.$root.activeUserHasRole('admin')
    },
    isDriver () {
      return this.activeUser.is_driver
    },
    isOrgSetupComplete () {
      return this.hasProviderConnections &&
        !this.licensesRequireSetup &&
        this.hasVehicles &&
        this.hasDrivers
    },
    isDriverSetupComplete () {
      return this.hasSavedSignature &&
        this.hasSavedDriversLicense &&
        this.hasSavedFaLicense
    },
    // ------------ Provider connections
    providerConnections () {
      return this.$store.getters['sh/data']('providerConnections')
    },
    providerConnectionItems () {
      return this.providerConnections ? this.providerConnections.items : []
    },
    hasProviderConnections () {
      return this.providerConnectionItems.length > 0
    },
    // ------------ Shipper info
    shipper () {
      return this.hasProviderConnections ? this.providerConnectionItems[0].shipper : null
    },
    shipperId () {
      return this.shipper?.id
    },
    shipperStatus () {
      return this.shipper ? this.shipper.shipper_status : 'REQUIRES_SETUP'
    },
    licensesRequireSetup () {
      return this.shipperStatus === 'REQUIRES_SETUP'
    },
    // ------------ Vehicles for org
    vehicleData () {
      return this.$store.getters['sh/data']('vehicles')
    },
    vehicleItems () {
      return this.vehicleData ? this.vehicleData.items : []
    },
    hasVehicles () {
      return this.vehicleItems.length > 0
    },
    // ------------ Drivers for org
    hasDrivers () {
      return this.memberData && this.memberData.items && this.memberData.items.length > 0
    },
    // ------ Active user info
    hasSavedSignature () {
      return this.activeUser.saved_signature
    },
    hasSavedDriversLicense () {
      return this.activeUser.saved_drivers_license
    },
    hasSavedFaLicense () {
      return this.activeUser.saved_fa_license
    }
  },

  async beforeMount () {
    this.isDataLoading = true
    const loaded = {}

    if (this.showOrgSetup) {
      await this.loadProviderConnections()
      await this.loadVehicles()
      await this.loadMemberDrivers()
      loaded.org_setup = this.isOrgSetupComplete
    }

    if (this.showDriverSetup) {
      loaded.driver_setup = this.isDriverSetupComplete
    }

    this.$emit('loaded', loaded)
    this.isDataLoading = false
  },

  methods: {
    goToUserSettingsPage () {
      this.$root.goTo('user-settings-profile')
    },

    // Load all provider connections
    async loadProviderConnections (force) {
      await this.$store.dispatch('sh/dataApi', {
        method: 'load',
        uri: 'provider-connections',
        opts: {
          resource: 'providerConnections',
          params: { with: 'shipper' },
          all: true,
          force
        }
      })
    },

    // Load all vehicles
    async loadVehicles (force) {
      await this.$store.dispatch('sh/dataApi', {
        method: 'load',
        uri: 'vehicles',
        opts: {
          resource: 'vehicles',
          all: true,
          force
        }
      })
    },

    async loadMemberDrivers () {
      this.memberData = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri: 'users',
        opts: { params: { is_driver: true } }
      })
    }
  }
}
</script>

<style scoped>
  .user-app-setup-guide .message-container {
    font-size: 14px;
    border: 1px solid #454545;
    border-radius: 4px;
    padding: 14px;
  }

  .user-app-setup-guide .message-heading {
    color: #c9c9c9;
    font-size: 1.14rem;
    font-weight: 500;
  }

  .user-app-setup-guide .action-item-text {
    font-size: 1.08rem;
  }

  .user-app-setup-guide .action-item-text.completed {
    color: #727272;
  }

  .user-app-setup-guide .action-item-icon {
    font-size: 28px;
  }

  .user-app-setup-guide .action-resolved-text {
    color: #4caf4f;
    font-size: 1.08rem;
    font-weight: 500;
  }
</style>
